import React, { useEffect, useState } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import {
  EArtAmmunitionType,
  EArtAncillaryType,
  EArtAncillaryTypeName,
  EArtComponentType,
} from 'constants/ArtAccessoryType';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes, ETransactionDictionaryItemTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ERouteLinks } from 'models/route';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { Stack, Typography } from '@mui/material';
import {
  getFieldsByAccessoriesType,
  getFieldsByAmmunitionType,
  getFieldsByComponentType,
} from 'pages/AncillariesPage/helpers';
import { colors } from 'theme/colors';
import { useQuery } from 'hooks/useQuery';
import { EPermission } from 'models/permissions';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { StyledNotesTypography } from '../styles';
import ImagesGallery from 'components/organisms/ImagesGallery';
import { EModuleType } from 'components/organisms/ImagesGallery/helper';
import { findInDictionary } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';

export enum EAccessoryLabels {
  ancillary = 'ancillariesReferenceTable.accordion.ancillary.column',
  accessoryType = 'ancillariesReferenceTable.accordion.accessoryType.column',
  artId = 'ancillariesReferenceTable.accordion.artId.column',
  hostType = 'ancillariesReferenceTable.accordion.hostType.column',
  manufacturer = 'ancillariesReferenceTable.accordion.accessoryManufacturer.column',
  productName = 'ancillariesReferenceTable.accordion.productName.column',
  calibre = 'ancillariesReferenceTable.accordion.calibre.column',
  composition = 'ancillariesReferenceTable.accordion.composition.column',
  proofHouse = 'ancillariesReferenceTable.accordion.proofMark.label',
  lightColour = 'ancillariesReferenceTable.accordion.lightColour.label',
  capacity = 'ancillariesReferenceTable.accordion.capacity.label',
  magnification = 'ancillariesReferenceTable.accordion.magnification.label',
  utility = 'ancillariesReferenceTable.accordion.utility.label',
  rectile = 'ancillariesReferenceTable.accordion.reticle.label',
  notes = 'ancillariesReferenceTable.accordion.notes.label',
}

export enum EAmmunitionLabels {
  ancillary = 'ancillariesReferenceTable.accordion.ancillary.column',
  artId = 'ancillariesReferenceTable.accordion.artId.column',
  ammunitionType = 'ammunitionTable.accordion.ammunitionType.label',
  manufacturer = 'ammunitionTable.accordion.ammunitionManufacturer.label',
  productName = 'ammunitionTable.accordion.productName.label',
  factory = 'ammunitionTable.accordion.factory.label',
  calibre = 'ammunitionTable.accordion.calibre.label',
  cartridgeComposition = 'ammunitionTable.accordion.catridgeComposition.label',
  colourOfPrimerSealant = 'ammunitionTable.accordion.colourOfPrimerSealant.label',
  projectileShape = 'ammunitionTable.accordion.projectileShape.label',
  functionalType = 'ammunitionTable.accordion.functionalType.label',
  shotSize = 'ammunitionTable.accordion.shotSize.label',
  munitionTypeUniqueId = 'ammunitionTable.accordion.munitionType.label',
  munitionVelocity = 'ammunitionTable.accordion.munitionVelocity.label',
  headstampYear = 'ammunitionTable.accordion.headstampYear.label',
  alphanumericalMarkings = 'ammunitionTable.accordion.alphanumericalMarkins.label',
  notes = 'ammunitionTable.accordion.notes.label',
  ancillaryTypeUniqueId = 'ammunitionDetails.ammunitionType.label',
  factoriesUniqueIds = 'ammunitionDetails.factories.label',
  manufacturersUniqueIds = 'ammunitionDetails.manufacturers.label',
  calibresUniqueIds = 'ammunitionTable.accordion.calibre.label',
}

export enum EComponentLabels {
  ancillary = 'ancillariesReferenceTable.accordion.ancillary.column',
  artId = 'ancillariesReferenceTable.accordion.artId.column',
  componentType = 'componentsTable.accordion.componentType.label',
  manufacturer = 'componentsTable.accordion.manufacturer.label',
  productName = 'componentsTable.accordion.productName.label',
  calibre = 'componentsTable.accordion.calibre.label',
  length = 'componentsTable.accordion.length.label',
  composition = 'componentsTable.accordion.composition.label',
  capacity = 'componentsTable.accordion.capacity.label',
  proofHouse = 'componentsTable.accordion.proofhouse.label',
  frtTypeUniqueId = 'componentsTable.accordion.firearmType.label',
  frtManufacturerUniqueId = 'componentsTable.accordion.firearmManufacturer.label',
  frtMakeUniqueId = 'componentsTable.accordion.make.label',
  frtModelUniqueId = 'componentsTable.accordion.model.label',
  notes = 'componentsTable.accordion.notes.label',
}

export enum EAncillaryLabels {
  ancillaryDictionaryUniqueId = 'accessoryDetails.ancillary.type.label',
  ancillaryTypeUniqueId = 'myFilters.ancillaries.type.label',
  manufacturerUniqueId = 'myFilters.ancillaries.manufacturer.label',
  calibresUniqueIds = 'accessoryDetails.calibre.label',
  hostTypesUniqueIds = 'accessoryDetails.hostType.label',
  magnificationsUniqueIds = 'accessoryDetails.magnification.label',
  utilitiesUniqueIds = 'accessoryDetails.utility.label',
  reticlseUniqueIds = 'accessoryDetails.reticle.label',
  lightColoursUniqueIds = 'accessoryDetails.lightcolour.label',
  compositionsUniqueIds = 'accessoryDetails.composition.label',
  proofHousesUniqueIds = 'accessoryDetails.proofMark.label',
  statesUniqueIds = 'myFilters.ancillaries.state.label',
  state = 'myFilters.ancillaries.state.label',
  legalityUniqueId = 'myFilters.ancillaries.legality.label',
  legality = 'myFilters.ancillaries.legality.label',
  ownerName = 'accessoryDetails.transactions.owner.column',
  owner = 'accessoryDetails.transactions.owner.column',
  ownerUniqueId = 'accessoryDetails.transactions.owner.column',
  keeperName = 'accessoryDetails.transactions.keeper.column',
  keeper = 'accessoryDetails.transactions.keeper.column',
  keeperUniqueId = 'accessoryDetails.transactions.keeper.column',
  serialNumber = 'accessoryDetails.serialNumber.label',
  exhibitNumber = 'accessoriesTable.accordion.exhibitNumber.label',
  ancillary = 'ancillariesReferenceTable.accordion.ancillary.column',
  accessoryType = 'ancillariesReferenceTable.accordion.accessoryType.column',
  artId = 'ancillariesReferenceTable.accordion.artId.column',
  hostType = 'ancillariesReferenceTable.accordion.hostType.column',
  manufacturer = 'ancillariesReferenceTable.accordion.accessoryManufacturer.column',
  calibre = 'ancillariesReferenceTable.accordion.calibre.column',
  composition = 'ancillariesReferenceTable.accordion.composition.column',
  proofHouse = 'ancillariesReferenceTable.accordion.proofMark.label',
  lightColour = 'ancillariesReferenceTable.accordion.lightColour.label',
  capacity = 'ancillariesReferenceTable.accordion.capacity.label',
  accessoryCapacity = 'ancillariesReferenceTable.accordion.capacity.label',
  magnification = 'ancillariesReferenceTable.accordion.magnification.label',
  utility = 'ancillariesReferenceTable.accordion.utility.label',
  rectile = 'ancillariesReferenceTable.accordion.reticle.label',
  fireCycleId = 'componentsTable.accordion.fireCycleId.label',
  capacityUniqueId = 'componentsTable.accordion.capacity.label',
  ammunitionType = 'Ammunition Type',
  factory = 'Factory',
  cartridgeComposition = 'Cartridge Composition',
  colourOfPrimerSealant = 'Colour of Primer Sealant',
  projectileShape = 'Projectile Shape',
  functionalType = 'Functional Type',
  shotSize = 'Shot Size',
  munitionType = 'Munition Type',
  munitionVelocity = 'Munition Velocity',
  alphanumericalMarkings = 'Alphanumerical Markings',
  lotNumber = 'ammunitionTable.accordion.lotNumber.label',
  batchNumber = 'ammunitionTable.accordion.batchNumber.label',
  ammunitionManufacturerUniqueId = 'ammunitionTable.accordion.ammunitionManufacturer.label',
  factoriesUniqueIds = 'ammunitionTable.accordion.factory.label',
  manufactureDateFrom = 'ammunitionTable.accordion.manufactureDateFrom.label',
  manufactureDateTo = 'ammunitionTable.accordion.manufactureDateTo.label',
  expiryDateFrom = 'ammunitionTable.accordion.expiryDateFrom.label',
  expiryDateTo = 'ammunitionTable.accordion.expiryDateTo.label',
  functionalTypeUniqueId = 'ammunitionTable.accordion.functionalType.label',
  cartridgeCompositionUniqueId = 'ammunitionTable.accordion.catridgeComposition.label',
  colourOfPrimerSealantUniqueId = 'ammunitionTable.accordion.colourOfPrimerSealant.label',
  projectileShapeUniqueId = 'ammunitionTable.accordion.projectileShape.label',
  shotSizeUniqueId = 'ammunitionTable.accordion.shotSize.label',
  munitionTypesUniqueIds = 'ammunitionTable.accordion.munitionType.label',
  munitionVelocitiesUniqueIds = 'ammunitionTable.accordion.munitionVelocity.label',
  manufacturersUniqueIds = 'ammunitionTable.accordion.ammunitionManufacturer.label',
  headstampYear = 'ammunitionTable.accordion.headstampYear.label',
  alphanumericalMarkins = 'ammunitionTable.accordion.alphanumericalMarkins.label',
  componentType = 'Component Type',
  length = 'componentsTable.accordion.length.label',
  componentCapacitiesUniqueIds = 'componentsTable.accordion.capacity.label',
  frtTypesUniqueIds = 'componentsTable.accordion.frtType.label',
  frtMakesUniqueIds = 'componentsTable.accordion.frtMake.label',
  frtModelsUniqueIds = 'componentsTable.accordion.frtModel.label',
  frtManufacturersUniqueIds = 'componentsTable.accordion.frtManufacturer.label',
  productName = 'ammunitionTable.accordion.productName.column',
  marking = 'ancillariesTable.accordion.marking.label',
  ammunitionTypeUniqueId = 'ammunitionTable.accordion.ammunitionType.label',
}

export enum EAncillaryComponentLabels {
  ancillaryTypeUniqueId = 'componentsTable.accordion.componentType.label',
  fireCycleId = 'componentsTable.accordion.fireCycleId.label',
  serialNumber = 'componentsTable.accordion.serialNumber.label',
  exhibitNumber = 'componentsTable.accordion.exhibitNumber.label',
  manufacturersUniqueIds = 'componentsTable.accordion.componentManufacturer.label',
  calibresUniqueIds = 'componentsTable.accordion.calibre.label',
  length = 'componentsTable.accordion.length.label',
  compositionsUniqueIds = 'componentsTable.accordion.composition.label',
  proofHousesUniqueIds = 'componentsTable.accordion.proofMark.label',
  statesUniqueIds = 'componentsTable.accordion.componentState.label',
  legalityUniqueId = 'componentsTable.accordion.componentLegality.label',
  owner = 'componentsTable.accordion.owner.label',
  keeper = 'componentsTable.accordion.keeper.label',
  frtTypesUniqueIds = 'componentsTable.accordion.frtType.label',
  frtMakesUniqueIds = 'componentsTable.accordion.frtMake.label',
  frtModelsUniqueIds = 'componentsTable.accordion.frtModel.label',
  frtManufacturersUniqueIds = 'componentsTable.accordion.frtManufacturer.label',
  componentCapacitiesUniqueIds = 'componentsTable.accordion.capacity.label',
  ownerName = 'accessoryDetails.transactions.owner.column',
  ownerUniqueId = 'accessoryDetails.transactions.owner.column',
  keeperName = 'accessoryDetails.transactions.keeper.column',
  keeperUniqueId = 'accessoryDetails.transactions.keeper.column',
  productName = 'ammunitionTable.accordion.productName.column',
  marking = 'ancillariesTable.accordion.marking.label',
}

const DetailAncillariesReference = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const navigate = useNavigate();
  const [fields, setFields] = useState<string[] | undefined>(undefined);
  const transactionPagePath = useMatch(ERouteLinks.LegalEntityViewAncillary);
  const isTransactionPage = !!transactionPagePath;
  const queryParams = useQuery();
  const typeLE: string | null = queryParams.get('type');
  const ancillaryDetails = useMatch(ERouteLinks.ViewAncillaryDetails);

  const {
    loadDictionaries,
    artAccessoryDetail,
    artAmmunitionDetail,
    artComponentDetail,
    getARTAccessoryDetail,
    getARTAmmunitionDetail,
    getARTComponentDetail,
  } = useAncillaries();

  const {
    getMake,
    make,
    getModel,
    model,
    getManufacturer,
    manufacturer,
    getFirearmType,
    firearmType,
  } = useDictionary();

  useEffect(() => {
    getMake();
    getModel();
    getManufacturer();
    getFirearmType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (type && id) {
      if (type === EArtAncillaryTypeName.ACCESSORY.toLowerCase()) {
        (async () => {
          await getARTAccessoryDetail(id);
        })();
      } else if (type === EArtAncillaryTypeName.AMMUNITION.toLowerCase()) {
        (async () => {
          await getARTAmmunitionDetail(id);
        })();
      } else if (type === EArtAncillaryTypeName.COMPONENTS.toLowerCase()) {
        (async () => {
          await getARTComponentDetail(id);
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  useEffect(() => {
    if (artAccessoryDetail?.accessoryTypeUniqueId) {
      setFields(getFieldsByAccessoriesType(artAccessoryDetail?.accessoryTypeUniqueId));
    } else if (artAmmunitionDetail?.ammunitionTypeUniqueId) {
      setFields(
        getFieldsByAmmunitionType(
          artAmmunitionDetail?.ammunitionTypeUniqueId as EArtAmmunitionType,
        ),
      );
    } else if (artComponentDetail?.componentTypeUniqueId) {
      setFields(
        getFieldsByComponentType(
          artComponentDetail?.componentTypeUniqueId as EArtComponentType,
        ),
      );
    }
  }, [artAccessoryDetail, artAmmunitionDetail, artComponentDetail]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.ArtAncillaryOptions,
        EDictionaryTypes.FirearmType,
        EDictionaryTypes.Make,
        EDictionaryTypes.Model,
        EDictionaryTypes.Manufacturer,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  return (
    <div>
      {type === EArtAncillaryTypeName.ACCESSORY.toLowerCase() && (
        <>
          {!ancillaryDetails && (
            <Breadcrumbs
              items={[
                {
                  label: t('module.ancillaries.name'),
                  route: ERouteLinks.Ancillaries,
                },
                {
                  label: t('module.ancillariesreferencetable.name'),
                  route: `${ERouteLinks.Ancillaries}?expand=ancillaries-reference-table&ancillaryOptionName=${EArtAncillaryType.ACCESSORIES}`,
                },
                t('accessoryDetails.accordion.title.label'),
              ]}
            />
          )}
          {artAccessoryDetail && fields && (
            <Grid sx={{ gap: 3 }}>
              <StyledPageTitle variant="h4">
                {t('accessoryDetails.accordion.title.label')}
              </StyledPageTitle>
              <Grid size={{ xs: 12 }}>
                <DetailsTemplate
                  title={artAccessoryDetail?.accessoryType}
                  pageType={
                    isTransactionPage
                      ? EDetailsPageTypes.transactionSelectView
                      : EDetailsPageTypes.view
                  }
                  redirectToSelect={() => {
                    navigate(
                      `${ERouteLinks.LegalEntityAddAncillary.replace(
                        ':id',
                        transactionPagePath?.params?.leId ?? '',
                      )}?type=${typeLE}&expand=ancillaries`,
                      {
                        state: {
                          ancillary: artAccessoryDetail,
                          ancillaryType: EArtAncillaryTypeName.ACCESSORY,
                          prevRoute: 'prevRoute',
                        },
                      },
                    );
                  }}
                  editPermissions={[EPermission.MANAGE_LOCAL_ART]}
                  redirectToEditView={
                    !ancillaryDetails
                      ? () => {
                          navigate(
                            ERouteLinks.EditAncillaries.replace(
                              ':id',
                              artAccessoryDetail.uniqueId,
                            ).replace(':type', EArtAncillaryTypeName.ACCESSORY.toLowerCase()),
                          );
                        }
                      : undefined
                  }
                >
                  <Grid
                    container
                    sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, justifyContent: 'center' }}
                    spacing="20px"
                  >
                    <Grid
                      container
                      sx={{
                        gap: '12px',
                        justifyContent: 'center',
                        alignContent: 'flex-start',
                        width: '270px',
                      }}
                    >
                      <ImagesGallery
                        moduleType={EModuleType.ART}
                        ownerId={artAccessoryDetail.uniqueId}
                        buttonId={'art_accessory_details-add_photo'}
                        buttonPermission={EPermission.MANAGE_LOCAL_ART}
                        onClickEdit={() =>
                          navigate(
                            `${ERouteLinks.AncillariesEditImages.replace(
                              ':type',
                              EArtAncillaryTypeName.ACCESSORY.toLowerCase() as string,
                            ).replace(':id', id as string)}`,
                          )
                        }
                        onClickAdd={() =>
                          navigate(
                            `${ERouteLinks.AncillariesAddImages.replace(
                              ':type',
                              EArtAncillaryTypeName.ACCESSORY.toLowerCase() as string,
                            ).replace(':id', id as string)}`,
                          )
                        }
                      />
                    </Grid>
                    <Grid
                      container
                      sx={{
                        width: { xs: '100%', md: 'calc(100% - 270px)' },
                        rowGap: 1,
                        alignContent: 'flex-start',
                      }}
                    >
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.ancillary)}
                          </Typography>
                          <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                            {ETransactionDictionaryItemTypes.ACCESSORY.toLocaleLowerCase()}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.accessoryType)}
                          </Typography>
                          <Typography variant="body2">
                            {artAccessoryDetail?.accessoryType}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.artId)}
                          </Typography>
                          <Typography variant="body2">{artAccessoryDetail?.artId}</Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.manufacturer)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAccessoryDetail?.manufacturer)}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.productName)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAccessoryDetail?.productName)}
                          </Typography>
                        </Stack>
                      </Grid>

                      {fields.includes('hostTypeUniqueId') && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.hostType)}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(artAccessoryDetail?.hostType)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      {fields.includes('calibreUniqueId') && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.calibre)}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(artAccessoryDetail?.calibre)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      {fields.includes('compositionUniqueId') && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.composition)}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(artAccessoryDetail?.composition)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      {fields.includes('proofHouseUniqueId') && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.proofHouse)}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(artAccessoryDetail?.proofHouse)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      {fields.includes('lightColourUniqueId') && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.lightColour)}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(artAccessoryDetail?.lightColour)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      {fields.includes('capacity') && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.capacity)}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(artAccessoryDetail?.capacity)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      {fields.includes('magnificationUniqueId') && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.magnification)}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(artAccessoryDetail?.magnification)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      {fields.includes('utilityUniqueId') && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.utility)}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(artAccessoryDetail?.utility)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      {fields.includes('rectileUniqueId') && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.rectile)}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(artAccessoryDetail?.rectile)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      {fields.includes('notes') && (
                        <Grid size={{ xs: 12 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(EAccessoryLabels.notes)}
                            </Typography>
                            <Typography variant="body2">
                              {getHtmlText(artAccessoryDetail?.notes)}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </DetailsTemplate>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {type === EArtAncillaryTypeName.AMMUNITION.toLowerCase() && (
        <>
          {!ancillaryDetails && (
            <Breadcrumbs
              items={[
                {
                  label: t('module.ancillaries.name'),
                  route: ERouteLinks.Ancillaries,
                },
                {
                  label: t('module.ancillariesreferencetable.name'),
                  route: `${ERouteLinks.Ancillaries}?expand=ancillaries-reference-table&ancillaryOptionName=${EArtAncillaryType.AMMUNITION}`,
                },
                t('module.ammunitionDetails.name'),
              ]}
            />
          )}
          {artAmmunitionDetail && fields && (
            <Grid sx={{ gap: 3 }}>
              <StyledPageTitle variant="h4">
                {t('module.ammunitionDetails.name')}
              </StyledPageTitle>
              <DetailsTemplate
                title={artAmmunitionDetail?.ammunitionType}
                pageType={
                  isTransactionPage
                    ? EDetailsPageTypes.transactionSelectView
                    : EDetailsPageTypes.view
                }
                redirectToSelect={() => {
                  navigate(
                    `${ERouteLinks.LegalEntityAddAncillary.replace(
                      ':id',
                      transactionPagePath?.params?.leId ?? '',
                    )}?type=${typeLE}&expand=ancillaries`,
                    {
                      state: {
                        ancillary: artAmmunitionDetail,
                        ancillaryType: EArtAncillaryTypeName.AMMUNITION,
                        prevRoute: 'prevRoute',
                      },
                    },
                  );
                }}
                editPermissions={[EPermission.MANAGE_LOCAL_ART]}
                redirectToEditView={
                  !ancillaryDetails
                    ? () => {
                        navigate(
                          ERouteLinks.EditAncillaries.replace(
                            ':id',
                            artAmmunitionDetail.uniqueId,
                          ).replace(':type', EArtAncillaryTypeName.AMMUNITION.toLowerCase()),
                        );
                      }
                    : undefined
                }
              >
                <Grid
                  container
                  sx={{
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    justifyContent: 'center',
                    gap: '20px',
                  }}
                >
                  <Grid
                    sx={{
                      gap: '12px',
                      justifyContent: 'center',
                      alignContent: 'flex-start',
                      width: '270px',
                    }}
                  >
                    <ImagesGallery
                      moduleType={EModuleType.ART}
                      ownerId={artAmmunitionDetail.uniqueId}
                      buttonId={'art_ammunition_details-add_photo'}
                      buttonPermission={EPermission.MANAGE_LOCAL_ART}
                      onClickEdit={() =>
                        navigate(
                          `${ERouteLinks.AncillariesEditImages.replace(
                            ':type',
                            EArtAncillaryTypeName.AMMUNITION.toLowerCase() as string,
                          ).replace(':id', id as string)}`,
                        )
                      }
                      onClickAdd={() =>
                        navigate(
                          `${ERouteLinks.AncillariesAddImages.replace(
                            ':type',
                            EArtAncillaryTypeName.AMMUNITION.toLowerCase() as string,
                          ).replace(':id', id as string)}`,
                        )
                      }
                    />
                  </Grid>
                  <Grid
                    container
                    sx={{
                      width: { xs: '100%', md: 'calc(100% - 270px)' },
                      rowGap: 1,
                      alignContent: 'flex-start',
                    }}
                  >
                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.ancillary)}
                        </Typography>
                        <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                          {ETransactionDictionaryItemTypes.AMMUNITION.toLocaleLowerCase()}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.ammunitionType)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(artAmmunitionDetail?.ammunitionType)}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.artId)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(artAmmunitionDetail?.artId)}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.manufacturer)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(artAmmunitionDetail?.manufacturer)}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.productName)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(artAmmunitionDetail?.productName)}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.factory)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(artAmmunitionDetail?.factory)}
                        </Typography>
                      </Stack>
                    </Grid>
                    {fields.includes('calibreUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.calibre)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAmmunitionDetail?.calibre)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('cartridgeCompositionUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.cartridgeComposition)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAmmunitionDetail?.cartridgeComposition)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('colourOfPrimerSealantUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.colourOfPrimerSealant)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAmmunitionDetail?.colourOfPrimerSealant)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('projectileShapeUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.projectileShape)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAmmunitionDetail?.projectileShape)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('functionalTypeUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.functionalType)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAmmunitionDetail?.functionalType)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('shotSizeUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.shotSize)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAmmunitionDetail?.shotSize)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('munitionTypeUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.munitionTypeUniqueId)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAmmunitionDetail?.munitionType)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('munitionVelocityUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.munitionVelocity)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAmmunitionDetail?.munitionVelocity)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('alphanumericalMarkings') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.alphanumericalMarkings)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artAmmunitionDetail?.alphanumericalMarkings)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('notes') && (
                      <Grid size={{ xs: 12 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.notes)}
                          </Typography>
                          <StyledNotesTypography
                            variant="body2"
                            style={{ wordBreak: 'break-all' }}
                          >
                            {getHtmlText(artAmmunitionDetail?.notes)}
                          </StyledNotesTypography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </DetailsTemplate>
            </Grid>
          )}
        </>
      )}

      {type === EArtAncillaryTypeName.COMPONENTS.toLowerCase() && (
        <>
          {!ancillaryDetails && (
            <Breadcrumbs
              items={[
                {
                  label: t('module.ancillaries.name'),
                  route: ERouteLinks.Ancillaries,
                },
                {
                  label: t('module.ancillariesreferencetable.name'),
                  route: `${ERouteLinks.Ancillaries}?expand=ancillaries-reference-table&ancillaryOptionName=${EArtAncillaryType.COMPONENTS}`,
                },
                t('module.componentDetails.name'),
              ]}
            />
          )}

          {artComponentDetail && fields && (
            <Grid sx={{ gap: 3 }}>
              <StyledPageTitle variant="h4">
                {t('module.componentDetails.name')}
              </StyledPageTitle>
              <DetailsTemplate
                title={artComponentDetail?.componentType}
                pageType={
                  isTransactionPage
                    ? EDetailsPageTypes.transactionSelectView
                    : EDetailsPageTypes.view
                }
                redirectToSelect={() => {
                  navigate(
                    `${ERouteLinks.LegalEntityAddAncillary.replace(
                      ':id',
                      transactionPagePath?.params?.leId ?? '',
                    )}?type=${typeLE}&expand=ancillaries`,
                    {
                      state: {
                        ancillary: artComponentDetail,
                        ancillaryType: EArtAncillaryTypeName.COMPONENTS,
                        prevRoute: 'prevRoute',
                      },
                    },
                  );
                }}
                editPermissions={[EPermission.MANAGE_LOCAL_ART]}
                redirectToEditView={
                  !ancillaryDetails
                    ? () => {
                        navigate(
                          ERouteLinks.EditAncillaries.replace(
                            ':id',
                            artComponentDetail.uniqueId,
                          ).replace(':type', EArtAncillaryTypeName.COMPONENTS.toLowerCase()),
                        );
                      }
                    : undefined
                }
              >
                <Grid
                  container
                  sx={{
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    justifyContent: 'center',
                    gap: '20px',
                  }}
                >
                  <Grid
                    container
                    sx={{
                      gap: '12px',
                      justifyContent: 'center',
                      alignContent: 'flex-start',
                      width: '270px',
                    }}
                  >
                    <ImagesGallery
                      moduleType={EModuleType.ART}
                      ownerId={artComponentDetail.uniqueId}
                      buttonId={'art_component_details-add_photo'}
                      buttonPermission={EPermission.MANAGE_LOCAL_ART}
                      onClickEdit={() =>
                        navigate(
                          `${ERouteLinks.AncillariesEditImages.replace(
                            ':type',
                            EArtAncillaryTypeName.COMPONENTS.toLowerCase() as string,
                          ).replace(':id', id as string)}`,
                        )
                      }
                      onClickAdd={() =>
                        navigate(
                          `${ERouteLinks.AncillariesAddImages.replace(
                            ':type',
                            EArtAncillaryTypeName.COMPONENTS.toLowerCase() as string,
                          ).replace(':id', id as string)}`,
                        )
                      }
                    />
                  </Grid>
                  <Grid
                    container
                    sx={{
                      width: { xs: '100%', md: 'calc(100% - 270px)' },
                      rowGap: 1,
                      alignContent: 'flex-start',
                    }}
                  >
                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.ancillary)}
                        </Typography>
                        <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                          {ETransactionDictionaryItemTypes.COMPONENT.toLocaleLowerCase()}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.componentType)}
                        </Typography>
                        <Typography variant="body2">
                          {artComponentDetail?.componentType}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.artId)}
                        </Typography>
                        <Typography variant="body2">{artComponentDetail?.artId}</Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.manufacturer)}
                        </Typography>
                        <Typography variant="body2">
                          {artComponentDetail?.manufacturer}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.productName)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(artComponentDetail?.productName)}
                        </Typography>
                      </Stack>
                    </Grid>
                    {fields.includes('calibreUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.calibre)}
                          </Typography>
                          <Typography variant="body2">
                            {artComponentDetail?.calibre}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('length') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.length)}
                          </Typography>
                          <Typography variant="body2">{artComponentDetail?.length}</Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('compositionUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.composition)}
                          </Typography>
                          <Typography variant="body2">
                            {artComponentDetail?.composition}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('proofHouseUniqueId') && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.proofHouse)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(artComponentDetail?.proofHouse)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {fields.includes('notes') && (
                      <Grid size={{ xs: 12 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.notes)}
                          </Typography>
                          <Typography variant="body2">
                            {getHtmlText(artComponentDetail?.notes)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}

                    <Grid
                      container
                      size={{ xs: 12 }}
                      sx={{
                        borderTop: `1px solid ${colors.primary100}`,
                        marginTop: '30px',
                        paddingTop: '25px',
                      }}
                    >
                      <Grid size={{ xs: 12 }}>
                        <Stack>
                          <StyledPageTitle variant="h5" sx={{ marginBottom: '20px' }}>
                            {t('ancillaries.accordion.firearmInformation.label')}
                          </StyledPageTitle>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.frtTypeUniqueId)}
                          </Typography>
                          <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                            {findInDictionary(
                              artComponentDetail?.frtTypeUniqueId,
                              firearmType,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.frtManufacturerUniqueId)}
                          </Typography>
                          <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                            {findInDictionary(
                              artComponentDetail?.frtManufacturerUniqueId,
                              manufacturer,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.frtMakeUniqueId)}
                          </Typography>
                          <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                            {findInDictionary(artComponentDetail?.frtMakeUniqueId, make)}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, lg: 3 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.frtModelUniqueId)}
                          </Typography>
                          <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                            {findInDictionary(artComponentDetail?.frtModelUniqueId, model)}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </DetailsTemplate>
            </Grid>
          )}
        </>
      )}
    </div>
  );
};

export default DetailAncillariesReference;
